import { lazy, Suspense } from "react";
import PageOwnership from "./modules/PageOwnership";
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  type Location,
} from "react-router-dom";

import { QuoteJourneyWorkspace } from "./components/quote/journey/QuoteJourneyWorkspace";
import RequireAuth from "./components/auth/RequireAuth";
import { QUOTE_SETTINGS_ROLES } from "./utils/quoteSettingsRoles";
import {
  QUOTE_SETTINGS_PATHS,
  type QuoteSettingsSection,
} from "./modules/quickquote/settings/quoteSettingsPaths";

// Route modules are loaded only when visited. This is real navigation-level
// code splitting: it keeps the initial shell small and lets Rollup place each
// page's Ant Design components with the page instead of one forced 1.1 MB
// framework chunk.
const CncLayout = lazy(() => import("./layouts/CncLayout"));
const Workbench = lazy(() => import("./pages/cnc/Workbench"));
const CadDrawingPage = lazy(() => import("./pages/cnc/CadDrawingPage"));
const GenerateModelPage = lazy(() => import("./pages/cnc/GenerateModelPage"));
const RedesignPage = lazy(() => import("./pages/cnc/RedesignPage"));
const QuotePage = lazy(() => import("./pages/cnc/QuotePage"));
const LineQuotePage = lazy(() => import("./pages/cnc/LineQuotePage"));
// 装配体多零件报价(方案第一阶段 M5)· 路由带 projectId,由上传装配体后建项目跳进来
const QuoteProjectRoute = lazy(
  () => import("./modules/quoteproject/QuoteProjectRoute"),
);
const LegacyQuoteProjectRedirect = lazy(() =>
  import("./modules/quoteproject/QuoteProjectRoute").then((module) => ({
    default: module.LegacyQuoteProjectRedirect,
  })),
);
const ProjectWorkbench = lazy(() => import("./pages/cnc/ProjectWorkbench"));
const BatchReviewPage = lazy(() => import("./pages/cnc/BatchReviewPage"));
const UploadPage = lazy(() => import("./pages/cnc/Upload"));
const GeneratePage = lazy(() => import("./pages/cnc/GeneratePage"));
const ProgramDetailPage = lazy(() => import("./pages/cnc/ProgramDetail"));
const ProgramDiff = lazy(() => import("./pages/cnc/ProgramDiff"));
const SignoffQueue = lazy(() => import("./pages/cnc/SignoffQueue"));
const RuleEditor = lazy(() => import("./pages/cnc/RuleEditor"));
const Dashboard = lazy(() => import("./pages/cnc/Dashboard"));
const MachineManagementPage = lazy(
  () => import("./modules/machines/MachineManagementPage"),
);
const MachineBoardPage = lazy(() => import("./pages/cnc/MachineBoardPage"));
const MachineTelemetryPage = lazy(() => import("./pages/cnc/MachineTelemetry"));
const AuditPage = lazy(() => import("./pages/cnc/Audit"));
const RoiCalculator = lazy(() => import("./pages/cnc/RoiCalculator"));
const LoginPage = lazy(() => import("./pages/auth/Login"));
const RegisterPage = lazy(() => import("./pages/auth/Register"));
const AcceptInvitePage = lazy(() => import("./pages/auth/AcceptInvite"));
const SharedQuotePage = lazy(() => import("./pages/share/SharedQuotePage"));
const ForbiddenPage = lazy(() => import("./pages/auth/Forbidden"));
const UsersAdminPage = lazy(() => import("./pages/admin/UsersAdmin"));
const TenantSettingsPage = lazy(() => import("./pages/admin/TenantSettings"));
const BillingPage = lazy(() => import("./pages/admin/BillingPage"));
import { BILLING_MONEY_ROLES } from "./pages/admin/billingView";
import { loadAgentConversation } from "./modules/agent/loadAgentConversation";
const ModelServiceSettingsPage = lazy(
  () => import("./pages/admin/ModelServiceSettings"),
);
const CustomersPage = lazy(() => import("./pages/admin/CustomersPage"));
const ProjectsPage = lazy(() => import("./pages/admin/ProjectsPage"));
const PartsPage = lazy(() => import("./pages/admin/PartsPage"));
const IncompleteToolsPage = lazy(
  () => import("./pages/admin/IncompleteToolsPage"),
);
const ToolImagesPage = lazy(() => import("./pages/admin/ToolImagesPage"));
const QuoteSettingsRoute = lazy(
  () => import("./modules/quickquote/settings/QuoteSettingsRoute"),
);
const WorkstationConfigsPage = lazy(
  () => import("./pages/admin/WorkstationConfigsPage"),
);
const EdgeGatewaysPage = lazy(() => import("./pages/admin/EdgeGatewaysPage"));
const EdgeGatewayDetailPage = lazy(
  () => import("./pages/admin/EdgeGatewayDetailPage"),
);
const EdgeMachineWizardPage = lazy(
  () => import("./pages/admin/EdgeMachineWizardPage"),
);
const TenantSkillsPage = lazy(() => import("./pages/admin/TenantSkillsPage"));
// 平台运营台 (/platform/*) · 方案 §5.2-f · 与租户面完全隔离:
// 独立 token 存储键(zq.platform.access_token)· 守卫在 PlatformLayout 内部
// (不复用 RequireAuth / AuthContext / apiClient)
const PlatformLogin = lazy(() => import("./platform/PlatformLogin"));
const PlatformLayout = lazy(() => import("./platform/PlatformLayout"));
const PlatformTenantsPage = lazy(() => import("./platform/TenantsPage"));
const PlatformTenantUsagePage = lazy(
  () => import("./platform/TenantUsagePage"),
);
const PlatformAuditPage = lazy(() => import("./platform/AuditPage"));
const PlatformGrantsPage = lazy(() => import("./platform/GrantsPage"));
const PlatformCockpitPage = lazy(
  () => import("./platform/cockpit/PlatformCockpitPage"),
);
// 智擎新界面 (/app/*) · 加法式，与上面 /cnc 完全隔离
const AppShell = lazy(() => import("./layouts/AppShell"));
const AgentHome = lazy(() => import("./modules/agent/AgentHome"));
// 首页会提前下好这个分包(见 loadAgentConversation)
const AgentConversation = lazy(loadAgentConversation);
const UnifiedCockpit = lazy(() => import("./modules/cockpit/UnifiedCockpit"));
// 旧版驾驶舱（PR #974 之前的样子）· 只留链接 /app/cockpit-legacy，侧边栏入口指向新版
const LegacyCockpit = lazy(
  () => import("./modules/cockpitLegacy/UnifiedCockpit"),
);
const RealSlotHub = lazy(() => import("./modules/realslots/RealSlotHub"));
const StaticModuleRouter = lazy(() => import("./modules/StaticModuleRouter"));
const DataHealthPage = lazy(
  () => import("./modules/dataHealth/DataHealthPage"),
);
const OrdersPage = lazy(() => import("./modules/orders/OrdersPage"));
const FactoryCalendarPage = lazy(
  () => import("./modules/factoryCalendar/FactoryCalendarPage"),
);
const ProjectLedgerPage = lazy(
  () => import("./modules/projects/ProjectLedgerPage"),
);

function LegacyQuoteSettingsRedirect({
  section,
}: {
  section: QuoteSettingsSection;
}) {
  const { search, hash } = useLocation();
  return (
    <Navigate to={`${QUOTE_SETTINGS_PATHS[section]}${search}${hash}`} replace />
  );
}

function ApplicationRoutes({ location }: { location: Location }) {
  return (
    <Suspense fallback={<div role="status">页面加载中…</div>}>
      <Routes location={location}>
        {/* 公开路由 · 未登录可访 */}
        <Route path="/roi" element={<RoiCalculator />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/invite/:token" element={<AcceptInvitePage />} />
        <Route path="/share/quote/:token" element={<SharedQuotePage />} />
        <Route path="/403" element={<ForbiddenPage />} />

        {/* 受保护路由 · 需登录 */}
        <Route path="/" element={<Navigate to="/app/home" replace />} />
        <Route
          path="/cnc"
          element={
            <RequireAuth>
              <CncLayout />
            </RequireAuth>
          }
        >
          <Route index element={<Navigate to="workbench" replace />} />
          <Route path="workbench" element={<Workbench />} />
          <Route path="generate-model" element={<GenerateModelPage />} />
          <Route path="redesign" element={<RedesignPage />} />
          <Route path="redesign/:cadDocumentId" element={<RedesignPage />} />
          <Route path="quotes" element={<QuotePage />} />
          <Route path="line-quotes" element={<LineQuotePage />} />
          <Route
            path="quote-projects/:projectId"
            element={<LegacyQuoteProjectRedirect />}
          />
          <Route
            path="projects/:projectId/graph"
            element={<ProjectWorkbench />}
          />
          <Route path="drawing" element={<CadDrawingPage />} />
          <Route path="drawing-batch" element={<BatchReviewPage />} />
          <Route path="upload" element={<UploadPage />} />
          <Route path="generate" element={<GeneratePage />} />
          <Route path="programs/diff" element={<ProgramDiff />} />
          <Route path="programs/:programId" element={<ProgramDetailPage />} />
          <Route path="signoff" element={<SignoffQueue />} />
          <Route path="dashboard" element={<Dashboard />} />
          <Route
            path="machines"
            element={<Navigate to="/app/machines" replace />}
          />
          {/* 账单与用量 · 金额是商务信息。角色集合与后端 billing.read_money
              共用 BILLING_MONEY_ROLES —— 只挡前端不挡 API 是纸糊的门 */}
          <Route
            path="billing"
            element={
              <RequireAuth allowedRoles={[...BILLING_MONEY_ROLES]}>
                <BillingPage />
              </RequireAuth>
            }
          />
          <Route path="rules" element={<RuleEditor />} />
          <Route
            path="machines/:machineId/telemetry"
            element={<MachineTelemetryPage />}
          />
          <Route path="audit" element={<AuditPage />} />
        </Route>

        {/* 兼容入口必须在通用 admin 守卫之外，否则工厂主管会先被旧父路由拒绝。 */}
        <Route
          path="/admin/quote-catalogs"
          element={
            <RequireAuth>
              <LegacyQuoteSettingsRedirect section="catalogs" />
            </RequireAuth>
          }
        />
        <Route
          path="/admin/quote-calibration"
          element={
            <RequireAuth>
              <LegacyQuoteSettingsRedirect section="calibration" />
            </RequireAuth>
          }
        />

        {/* admin 专属 · 需 platform_ops / it_admin */}
        <Route
          path="/admin"
          element={
            <RequireAuth allowedRoles={["platform_ops", "it_admin"]}>
              <CncLayout />
            </RequireAuth>
          }
        >
          <Route path="users" element={<UsersAdminPage />} />
          <Route path="tenant" element={<TenantSettingsPage />} />
          <Route path="llm" element={<ModelServiceSettingsPage />} />
          <Route path="customers" element={<CustomersPage />} />
          <Route path="projects" element={<ProjectsPage />} />
          <Route path="parts" element={<PartsPage />} />
          <Route path="tool-images" element={<ToolImagesPage />} />
          <Route path="tools" element={<IncompleteToolsPage />} />
          <Route
            path="workstation-configs"
            element={<WorkstationConfigsPage />}
          />
          <Route path="edge-gateways" element={<EdgeGatewaysPage />} />
          <Route
            path="edge-gateways/:gatewayId"
            element={<EdgeGatewayDetailPage />}
          />
          <Route
            path="edge-gateways/:gatewayId/config-revisions/:revisionId"
            element={<EdgeGatewayDetailPage />}
          />
          <Route
            path="edge-gateways/:gatewayId/machines/new"
            element={<EdgeMachineWizardPage />}
          />
          <Route
            path="edge-gateways/:gatewayId/machines/:machineId/edit"
            element={<EdgeMachineWizardPage />}
          />
          <Route path="tenant-skills" element={<TenantSkillsPage />} />
        </Route>

        {/* 平台运营台 · 独立于租户 RequireAuth · 守卫在 PlatformLayout 内 */}
        <Route path="/platform/login" element={<PlatformLogin />} />
        <Route path="/platform" element={<PlatformLayout />}>
          <Route index element={<Navigate to="tenants" replace />} />
          <Route path="tenants" element={<PlatformTenantsPage />} />
          <Route
            path="tenants/:tenantId/usage"
            element={<PlatformTenantUsagePage />}
          />
          <Route path="audit" element={<PlatformAuditPage />} />
          <Route path="grants" element={<PlatformGrantsPage />} />
          {/* 超级驾驶舱 · 跨租户只读聚合(方案 §4) */}
          <Route path="cockpit" element={<PlatformCockpitPage />} />
        </Route>

        {/* 智擎新界面 · 加法式 · 老 /cnc 与 / 默认路由完全不动 */}
        <Route
          path="/app"
          element={
            <RequireAuth>
              <AppShell />
            </RequireAuth>
          }
        >
          <Route index element={<Navigate to="home" replace />} />
          <Route path="home" element={<AgentHome />} />
          <Route
            path="quickquote/projects/:projectId"
            element={
              <>
                <PageOwnership />
                <QuoteProjectRoute />
              </>
            }
          />
          <Route path="agent" element={<AgentConversation />} />
          <Route path="cockpit" element={<UnifiedCockpit />} />
          <Route path="cockpit-legacy" element={<LegacyCockpit />} />
          {/* 旧入口保留为兼容深链，统一收敛到驾驶舱对应工作视图。 */}
          <Route
            path="dashboard"
            element={<Navigate to="/app/cockpit" replace />}
          />
          <Route
            path="flywheel"
            element={<Navigate to="/app/cockpit?view=flywheel" replace />}
          />
          <Route path="machines" element={<MachineBoardPage />} />
          {/* 驾驶舱瘦身迁出（docs/design/cockpit-slimming-2026-10-29）· 所有登录用户只读 */}
          <Route path="data-health" element={<DataHealthPage />} />
          <Route
            path="projects"
            element={
              <>
                <PageOwnership />
                <ProjectLedgerPage />
              </>
            }
          />
          {/* 驾驶舱接真实数据(c179)· 订单交付登记、工厂班次 · 登录可看,写入按角色 */}
          <Route path="orders" element={<OrdersPage />} />
          <Route path="factory-calendar" element={<FactoryCalendarPage />} />
          <Route
            path="machines/manage"
            element={
              <RequireAuth allowedRoles={["platform_ops", "it_admin"]}>
                <MachineManagementPage />
              </RequireAuth>
            }
          />
          {/* 真实功能槽 · 深链进已有 /cnc · /admin 真实页（那些页完全没动） */}
          <Route path="process" element={<RealSlotHub id="process" />} />
          <Route path="cnc" element={<RealSlotHub id="cnc" />} />
          <Route path="verify" element={<RealSlotHub id="verify" />} />
          <Route path="admin" element={<RealSlotHub id="admin" />} />
          <Route
            path="quickquote/settings/rates"
            element={
              <>
                <PageOwnership />
                <QuoteSettingsRoute section="rates" />
              </>
            }
          />
          <Route
            path="quickquote/settings/policy"
            element={
              <>
                <PageOwnership />
                <QuoteSettingsRoute section="policy" />
              </>
            }
          />
          <Route
            path="quickquote/settings/catalogs"
            element={
              <RequireAuth allowedRoles={[...QUOTE_SETTINGS_ROLES]}>
                <>
                  <PageOwnership />
                  <QuoteSettingsRoute section="catalogs" />
                </>
              </RequireAuth>
            }
          />
          <Route
            path="quickquote/settings/calibration"
            element={
              <RequireAuth allowedRoles={[...QUOTE_SETTINGS_ROLES]}>
                <>
                  <PageOwnership />
                  <QuoteSettingsRoute section="calibration" />
                </>
              </RequireAuth>
            }
          />
          {/* 9 个静态高保真模块（quote/reqs/mech/elec/plc/kb/connectors/ecn/mobile） */}
          <Route path=":moduleId" element={<StaticModuleRouter />} />
        </Route>
      </Routes>
    </Suspense>
  );
}

export default function App() {
  return (
    <QuoteJourneyWorkspace>
      {(location) => <ApplicationRoutes location={location} />}
    </QuoteJourneyWorkspace>
  );
}
